import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, output, signal, TrackByFunction } from '@angular/core'
import { UserRoles } from '@app/modules/auth/enums/user-roles.enum'
import { GenericTableComponent } from '@app/v2/shared/components/generic-table/generic-table.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { GenericTableCellDefDirective } from '@app/v2/shared/directives/generic-table-cell-def.directive'
import { GenericTableColumnDirective } from '@app/v2/shared/directives/generic-table-column.directive'
import { ModalService } from '@app/v2/shared/services/modal.service'

export const ROLES_PRIVILEGE_TABLE_MODAL_ID = 'roles-privilege-table-modal'

type RolePrivilegeRow = {
  id: string
  role: string
  owner: string
  [UserRoles.ADMIN]: string
  [UserRoles.COLLABORATOR]: string
  [UserRoles.ACCOUNTANT]: string
  [UserRoles.VIEWER]: string
}

@Component({
  selector: 'app-roles-table',
  imports: [ModalComponent, GenericTableComponent, GenericTableColumnDirective, GenericTableCellDefDirective, NgTemplateOutlet],
  standalone: true,
  templateUrl: './roles-table.component.html',
  styleUrl: './roles-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RolesTableComponent implements OnInit, OnDestroy {
  public readonly closed = output<void>()
  public readonly UserRoles = UserRoles
  public readonly headerIcon = signal<string>(`
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_36146_34918)">
      <path d="M6 6.31581C6.99411 6.31581 7.8 5.46751 7.8 4.42108C7.8 3.37465 6.99411 2.52635 6 2.52635C5.00589 2.52635 4.2 3.37465 4.2 4.42108C4.2 5.46751 5.00589 6.31581 6 6.31581Z" fill="#111928"/>
      <path d="M5.4 8.21054H6.6C7.23652 8.21054 7.84697 8.4767 8.29706 8.95048C8.74714 9.42425 9 10.0668 9 10.7368V12H3V10.7368C3 10.0668 3.25286 9.42425 3.70294 8.95048C4.15303 8.4767 4.76348 8.21054 5.4 8.21054Z" fill="#111928"/>
      <path d="M7.8 4.42108C7.79956 3.97455 7.64901 3.54259 7.37502 3.2017C7.10102 2.8608 6.72127 2.63297 6.303 2.55855" fill="#111928"/>
      <path d="M8.7 3.87851e-05C8.26891 0.00142604 7.84619 0.125407 7.47654 0.358874C7.10688 0.59234 6.80403 0.926614 6.6 1.32635C7.2772 1.47283 7.88571 1.86064 8.3229 2.42439C8.7601 2.98814 8.99924 3.69334 9 4.42108C8.999 4.62477 8.97891 4.82786 8.94 5.02739C9.5535 4.96594 10.1208 4.65776 10.5239 4.16702C10.9269 3.67628 11.1346 3.0408 11.1037 2.39292C11.0727 1.74504 10.8056 1.13468 10.3578 0.68894C9.91008 0.243203 9.31626 -0.00355851 8.7 3.87851e-05Z" fill="#111928"/>
      <path d="M4.4196 7.09391C3.90749 6.75631 3.50281 6.26545 3.2574 5.68423H3C2.20464 5.68523 1.44213 6.01826 0.879732 6.61026C0.317329 7.20226 0.000952714 8.0049 0 8.84212V10.1053C0 10.2728 0.0632141 10.4334 0.175736 10.5519C0.288258 10.6703 0.44087 10.7368 0.6 10.7368H1.8C1.80137 9.91249 2.05778 9.11104 2.53043 8.45375C3.00308 7.79646 3.66625 7.31911 4.4196 7.09391Z" fill="#111928"/>
      <path d="M9 5.68423H8.7426C8.49719 6.26545 8.09251 6.75631 7.5804 7.09391C8.33375 7.31911 8.99692 7.79646 9.46957 8.45375C9.94222 9.11104 10.1986 9.91249 10.2 10.7368H11.4C11.5591 10.7368 11.7117 10.6703 11.8243 10.5519C11.9368 10.4334 12 10.2728 12 10.1053V8.84212C11.999 8.0049 11.6827 7.20226 11.1203 6.61026C10.5579 6.01826 9.79536 5.68523 9 5.68423Z" fill="#111928"/>
      <path d="M3 4.42108C3.00076 3.69334 3.2399 2.98814 3.6771 2.42439C4.11429 1.86064 4.7228 1.47283 5.4 1.32635C5.20411 0.943487 4.91739 0.620529 4.56774 0.388916C4.21809 0.157303 3.81743 0.0249263 3.40476 0.00467119C2.99208 -0.0155839 2.58146 0.0769726 2.21285 0.273331C1.84425 0.46969 1.53022 0.763161 1.30133 1.12518C1.07244 1.4872 0.936496 1.90543 0.906725 2.33916C0.876954 2.77289 0.954372 3.20735 1.13144 3.60024C1.30851 3.99313 1.5792 4.33107 1.91717 4.58117C2.25513 4.83126 2.64885 4.98499 3.06 5.02739C3.02109 4.82786 3.001 4.62477 3 4.42108Z" fill="#111928"/>
      </g>
      <defs>
      <clipPath id="clip0_36146_34918">
      <rect width="12" height="12" fill="white"/>
      </clipPath>
      </defs>
      </svg>`)
  public readonly modalId = ROLES_PRIVILEGE_TABLE_MODAL_ID
  public readonly rows = signal<RolePrivilegeRow[]>([
    {
      id: 'view-database',
      role: 'Can view database',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'Yes',
      [UserRoles.VIEWER]: 'Yes',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    {
      id: 'edit-database',
      role: 'Can edit database',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'Yes',
      [UserRoles.VIEWER]: 'Yes',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    { id: 'create-events', role: 'Create Events', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'Yes', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    { id: 'edit-events', role: 'Edit Events', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'Yes', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    {
      id: 'archive-events',
      role: 'Archive Events',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'Yes',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    { id: 'delete-events', role: 'Delete Events', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'Yes', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    { id: 'clone-events', role: 'Clone Events', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'Yes', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    {
      id: 'restore-events',
      role: 'Restore Events',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'Yes',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    {
      id: 'create-sub-organization',
      role: 'Create sub-organization',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'No',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    {
      id: 'organization-settings',
      role: 'Organization settings',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'Yes',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'Yes'
    },
    { id: 'add-users', role: 'Add Users', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'Yes', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    {
      id: 'add-credit-cards',
      role: 'Can add credit cards',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'Yes',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'Yes'
    },
    {
      id: 'remove-credit-cards',
      role: 'Can Remove credit cards',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'No',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'Yes'
    },
    {
      id: 'create-or-change-subscriptions',
      role: 'Can create or change subscriptions',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'No',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'Yes'
    },
    { id: 'delete-viewer', role: 'Delete Viewer', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'Yes', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    { id: 'delete-admin', role: 'Delete Admin', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'No', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    {
      id: 'delete-collaborator',
      role: 'Delete Collaborator',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'No',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    {
      id: 'delete-accountant',
      role: 'Delete Accountant',
      owner: 'Yes',
      [UserRoles.ADMIN]: 'Yes',
      [UserRoles.COLLABORATOR]: 'No',
      [UserRoles.VIEWER]: 'No',
      [UserRoles.ACCOUNTANT]: 'No'
    },
    { id: 'export', role: 'Can export', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'No', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    { id: 'master-export', role: 'Master export', owner: 'Yes', [UserRoles.ADMIN]: 'Yes', [UserRoles.COLLABORATOR]: 'No', [UserRoles.VIEWER]: 'No', [UserRoles.ACCOUNTANT]: 'No' },
    {
      id: 'removed-by',
      role: 'Can be removed by',
      owner: 'No one',
      [UserRoles.ADMIN]: 'Owner, Admin',
      [UserRoles.COLLABORATOR]: 'Owner, Admin',
      [UserRoles.VIEWER]: 'Owner, Admin, Collaborator',
      [UserRoles.ACCOUNTANT]: 'Owner, Admin'
    }
  ])
  public readonly trackBy: TrackByFunction<RolePrivilegeRow> = (_index, row) => row.id
  public readonly rowKey = (row: RolePrivilegeRow): string => row.id

  private readonly _modalService = inject(ModalService)

  ngOnInit(): void {
    if (!this._modalService.isModalOpen(this.modalId)) this._modalService.toggleModal(this.modalId)
  }

  ngOnDestroy(): void {
    this._modalService.unregister(this.modalId)
  }

  /**
   * Notifies the parent to destroy this component after the modal closes.
   */
  public onModalClosed(): void {
    this.closed.emit()
  }

  /**
   * Whether a cell value should render as a Yes/No icon.
   * @param value {string} - The value to check.
   * @returns {boolean} - Whether the value should render as a Yes/No icon.
   */
  public isYesNoValue(value: string): boolean {
    return value === 'Yes' || value === 'No'
  }
}
